import { useId, type InputHTMLAttributes, type ReactNode, type Ref } from "react";
import { cn } from "../lib/cn";
import { FieldLabel } from "./FieldLabel";
import { useFormSkin } from "./FormSkinContext";
import { CheckCircleIcon, XIcon } from "./icons";

/**
 * Single-line field-shell classes per form skin (see FormSkinContext), shared with Select.
 *
 * Color, focus, error and valid classes don't vary by skin and stay in the
 * components. The enroll shell's `border-[1.5px]` overrides the base
 * `border-2` through twMerge.
 */
export const FIELD_SHELL_SKIN = {
  styleguide: "rounded-2xl px-4 py-3.5 text-[17px] leading-[1.5]",
  enroll: "h-[60px] rounded-[20px] border-[1.5px] px-5 text-[15px] leading-[1.5]",
} as const;

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  /** Show the required-asterisk in the label. Decoupled from the native `required` attribute. */
  required?: boolean | undefined;
  /** Marks the value as validated with a trailing check icon. Ignored while `error` is set. */
  valid?: boolean | undefined;
  /** Optional content rendered to the right of the label text. */
  labelAddon?: ReactNode | undefined;
  /** Render `labelAddon` inline right after the label text instead of far-right. */
  labelAddonInline?: boolean | undefined;
  /**
   * Visually hide the label; it stays for screen readers. For a control whose
   * question is a visible heading right above it, where a second label reads
   * as a duplicate. The hint, when given, stays visible.
   */
  labelHidden?: boolean | undefined;
  /** React 19 accepts `ref` as a regular prop on function components, so no forwardRef is needed. */
  ref?: Ref<HTMLInputElement> | undefined;
}

/**
 * Text input wrapper. Pair with react-hook-form via `<Input {...register("foo")} />`;
 * `register()` supplies `ref`, `name`, `onChange`, `onBlur` which the spread forwards.
 * Accepts native HTML `type` for `text | email | tel | date | number | url | password`.
 */
export function Input({
  ref,
  label,
  hint,
  error,
  required,
  valid,
  labelAddon,
  labelAddonInline,
  labelHidden,
  className,
  id,
  type = "text",
  ...rest
}: InputProps): ReactNode {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = error ? `${inputId}-error` : undefined;
  const showValid = Boolean(valid) && !error;
  const shell = FIELD_SHELL_SKIN[useFormSkin()];
  return (
    <div>
      <FieldLabel
        htmlFor={inputId}
        required={required}
        hint={hint}
        addon={labelAddon}
        addonInline={labelAddonInline}
        visuallyHidden={labelHidden}
      >
        {label}
      </FieldLabel>
      <div className="relative">
        <input
          ref={ref}
          id={inputId}
          type={type}
          aria-invalid={error ? true : undefined}
          aria-describedby={errorId}
          {...rest}
          className={cn(
            "border-border bg-input-bg text-foreground block w-full border-2",
            shell,
            "placeholder:text-placeholder",
            "focus:border-primary focus:bg-white focus:outline-none",
            "disabled:text-cloud-blue disabled:cursor-not-allowed disabled:opacity-50",
            showValid && "border-success/40 bg-success-bg pr-12",
            error && "border-danger/40 bg-danger-bg focus:border-danger",
            className,
          )}
        />
        {showValid && (
          <CheckCircleIcon
            size={18}
            className="text-success pointer-events-none absolute right-4 top-1/2 -translate-y-1/2"
          />
        )}
      </div>
      {error && (
        <p
          id={errorId}
          role="alert"
          className="text-danger mt-1.5 flex items-center gap-1.5 text-[13px] leading-[1.5]"
        >
          <XIcon size={13} className="shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
}
